import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface AdminUser {
  id: number;
  cognito_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  cognito_groups: string[];
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  role: string;
}

export interface ActivityEntry {
  id: number;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: unknown;
  ip_address: string | null;
  created_at: string;
  admin_email: string | null;
}

export interface ActivityResponse {
  entries: ActivityEntry[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: () => apiClient<AdminUser[]>('/admin-users'),
  });
}

export interface ActivityFilters {
  adminUserId?: number | null;
  search?: string;
}

export function useAdminActivity(page: number, filters: ActivityFilters = {}, limit = 50) {
  return useQuery({
    queryKey: ['admin-users', 'activity', page, limit, filters],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (filters.adminUserId) params.set('adminUserId', String(filters.adminUserId));
      if (filters.search) params.set('search', filters.search);
      return apiClient<ActivityResponse>(`/admin-users/activity?${params.toString()}`);
    },
  });
}

export interface InviteAdminInput {
  email: string;
  first_name: string;
  last_name: string;
  cognito_groups: string[];
}

// Both invite and reset-password hand back a one-time temporary password for the
// super-admin to convey out-of-band (Cognito's own email is suppressed).
export function useInviteAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteAdminInput) =>
      apiClient<AdminUser & { temporaryPassword: string }>('/admin-users', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}

export function useResetAdminPassword() {
  return useMutation({
    mutationFn: (id: number) =>
      apiClient<{ temporaryPassword: string }>(`/admin-users/${id}/reset-password`, { method: 'POST' }),
  });
}

export function useSetAdminActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      apiClient<AdminUser>(`/admin-users/${id}/set-active`, { method: 'POST', body: JSON.stringify({ is_active }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}
